import { useGetStationDisplayName } from "@/entities/station";
import { getRouteStationNames, TvvBesteldeRoute, TVVBestelStatus, VoertuigsoortDisplay } from "@/entities/tvv";
import { RoleGate, UserRole } from "@/features/auth";
import { BewerkOVRouteDialog } from "@/features/tvv/components/regievoerder/bestelling-bewerken/edit-ov-route-dialog.tsx";
import { BewerkBestellingDialog } from "@/features/tvv/components/regievoerder/bestelling-bewerken/edit-route-dialog.tsx";
import { StationEtaDisplay, TvvMessageBar, TvvRouteItemBase } from "@/features/tvv/components/shared";
import { ActionsDropdown, Button, DropdownMenuItem, ProgressCircleWithLabel, Separator } from "@/shared/components/ui";
import { useState } from "react";
import CommunicateEtas from "./communicate-etas";

type RegievoerderRouteItemProps = {
    dossierId: string;
    route: TvvBesteldeRoute;
    defaultOpen?: boolean;
    onStopRoute: () => void;
};

export function RegievoerderRouteItem({ dossierId, route, defaultOpen = false, onStopRoute }: Readonly<RegievoerderRouteItemProps>) {
    if (route.isOvRoute) {
        return <RegievoerderOvRouteItem dossierId={dossierId} route={route} defaultOpen={defaultOpen} onStopRoute={onStopRoute} />;
    } else {
        return <RegievoerderRegularRouteItem dossierId={dossierId} route={route} defaultOpen={defaultOpen} onStopRoute={onStopRoute} />;
    }
}
function RegievoerderOvRouteItem({ dossierId, route, defaultOpen = false, onStopRoute }: Readonly<RegievoerderRouteItemProps>) {
    const [editDialogOpen, setEditDialogOpen] = useState(false);

    return (
        <TvvRouteItemBase
            route={route}
            defaultOpen={defaultOpen}
            summaryText={route.status === TVVBestelStatus.Gestopt ? "Gestopt" : "Ingezet"}
        >
            <div className="grid grid-cols-12 gap-2">
                <OvRouteDetails opmerking={route.opmerking} voorwaarden={route.voorwaarden} />
                <RoleGate roles={[UserRole.REGIEVOERDER]}>
                    {route.status !== TVVBestelStatus.Gestopt && (
                        <div className="col-span-3 flex flex-row justify-end gap-2">
                            <div>
                                <Button variant="secondary" size="s" onClick={onStopRoute}>
                                    {VoertuigsoortDisplay[route.voertuigSoort].replace(" ", "-")} stoppen
                                </Button>
                            </div>
                            <ActionsDropdown>
                                <DropdownMenuItem onClick={() => setEditDialogOpen(true)}>Bestelling aanpassen</DropdownMenuItem>
                            </ActionsDropdown>
                        </div>
                    )}
                </RoleGate>
            </div>

            <TvvMessageBar dossierId={dossierId} status={route.status} actie={undefined} />

            {editDialogOpen && <BewerkOVRouteDialog route={route} open={editDialogOpen} setOpen={setEditDialogOpen} />}
        </TvvRouteItemBase>
    );
}

type OvRouteDetailsProps = {
    opmerking?: string;
    voorwaarden?: string;
};

function OvRouteDetails({ opmerking, voorwaarden }: Readonly<OvRouteDetailsProps>) {
    return (
        <div className="col-span-9 grid grid-cols-10 gap-2">
            {opmerking && (
                <>
                    <span className="text-s col-span-2 font-medium">Opmerkingen</span>
                    <span className="text-s col-span-8">{opmerking}</span>
                </>
            )}
            {voorwaarden && (
                <>
                    <span className="text-s col-span-2 font-medium">Voorwaarden</span>
                    <span className="text-s col-span-8">{voorwaarden}</span>
                </>
            )}
        </div>
    );
}

function RegievoerderRegularRouteItem({ dossierId, route, defaultOpen = false, onStopRoute }: Readonly<RegievoerderRouteItemProps>) {
    const getStationDisplayName = useGetStationDisplayName();
    const { beginStation, eindStation } = getRouteStationNames(route.stationCodes, getStationDisplayName);
    const actieveBussen = TvvBesteldeRoute.actieveAndAfgeschaaldeBussen(route)[0].length;
    const beginStationCode = TvvBesteldeRoute.getBeginStationCode(route);
    const eindStationCode = TvvBesteldeRoute.getEindStationCode(route);
    const beginStationBussen = TvvBesteldeRoute.getBussenForStation(route, beginStationCode);
    const eindStationBussen = TvvBesteldeRoute.getBussenForStation(route, eindStationCode);
    const bussenOpEindStation = TvvBesteldeRoute.getBussenOpEindStation(route);
    const routeEditable = TvvBesteldeRoute.routeEditable(route);
    const isComplete = TvvBesteldeRoute.isComplete(route);
    const moetAfgeschaaldWorden = TvvBesteldeRoute.moetAfgeschaaldWorden(route);
    const progressCircleType = moetAfgeschaaldWorden ? "default" : isComplete ? "check" : "default";
    const [editDialogOpen, setEditDialogOpen] = useState(false);

    const summaryText = route.status === TVVBestelStatus.Gestopt ? "Gestopt" : `${actieveBussen}/${route.bussenBesteld} geleverd`;

    return (
        <TvvRouteItemBase route={route} defaultOpen={defaultOpen} summaryText={summaryText}>
            <OpmerkingenVoorwaarden opmerking={route.opmerking} voorwaarden={route.voorwaarden} />

            <TvvMessageBar dossierId={dossierId} status={route.status} actie={undefined} />

            <div className="flex justify-between rounded-lg border p-4">
                <ProgressCircleWithLabel
                    total={route.bussenBesteld}
                    current={actieveBussen}
                    label="Geleverd"
                    unit="bussen"
                    type={progressCircleType}
                />
                <RoleGate roles={[UserRole.REGIEVOERDER]}>
                    {routeEditable && (
                        <div className="flex items-center gap-2 self-start">
                            <Button variant="secondary" onClick={onStopRoute} size="s">
                                Route stoppen
                            </Button>
                            <ActionsDropdown data-testid="route-actions">
                                <DropdownMenuItem onClick={() => setEditDialogOpen(true)}>Bestelling aanpassen</DropdownMenuItem>
                            </ActionsDropdown>
                        </div>
                    )}
                </RoleGate>
            </div>
            {routeEditable && (
                <RoleGate roles={[UserRole.REGIEVOERDER]}>
                    <CommunicateEtas route={route} />
                </RoleGate>
            )}

            <div className="rounded-lg border p-4">
                <div className="grid grid-cols-[1fr_min-content_1fr] gap-4" data-testid="stations-etas">
                    <StationEtaDisplay
                        stationNaam={beginStation}
                        aantalBussen={route.aantalBussenOpBeginStation}
                        bussen={beginStationBussen}
                        etasPubliek={route.etasPubliek}
                        data-testid="etas-beginstation"
                    />
                    <Separator orientation="vertical" className="border-default border" />
                    <StationEtaDisplay
                        stationNaam={eindStation}
                        aantalBussen={bussenOpEindStation}
                        bussen={eindStationBussen}
                        etasPubliek={route.etasPubliek}
                        data-testid="etas-eindstation"
                    />
                </div>
            </div>
            {editDialogOpen && <BewerkBestellingDialog route={route} open={editDialogOpen} setOpen={setEditDialogOpen} />}
        </TvvRouteItemBase>
    );
}

type OpmerkingenVoorwaardenProps = {
    opmerking?: string;
    voorwaarden?: string;
};

function OpmerkingenVoorwaarden({ opmerking, voorwaarden }: Readonly<OpmerkingenVoorwaardenProps>) {
    const [isExpanded, setIsExpanded] = useState(false);
    const maxLength = 100;
    const opmerkingTruncated = opmerking && opmerking.length > maxLength;
    const voorwaardenTruncated = voorwaarden && voorwaarden.length > maxLength;
    const needsTruncation = opmerkingTruncated || voorwaardenTruncated;
    const displayOpmerking = isExpanded ? opmerking : opmerking?.slice(0, maxLength);
    const displayVoorwaarden = isExpanded ? voorwaarden : voorwaarden?.slice(0, maxLength);

    return (
        <div className="text-s space-y-2">
            {opmerking && (
                <div>
                    <span className="text-s font-medium">Opmerking</span>{" "}
                    <span className="text-s whitespace-pre-line">
                        {displayOpmerking?.replaceAll(/\\\\n/g, "\n")}
                        {!isExpanded && opmerkingTruncated && "..."}
                    </span>
                </div>
            )}
            {voorwaarden && (
                <div>
                    <span className="text-s font-medium">Voorwaarden</span>{" "}
                    <span className="text-s">
                        {displayVoorwaarden}
                        {!isExpanded && voorwaardenTruncated && "..."}
                    </span>
                </div>
            )}
            {needsTruncation && (
                <button
                    type="button"
                    onClick={() => setIsExpanded(!isExpanded)}
                    className="text-content-interactive-default hover:underline"
                >
                    {isExpanded ? "Toon minder" : "Toon meer"}
                </button>
            )}
        </div>
    );
}
